import { sanitize as sanitizeLink } from 'quill/formats/link';
import type { ImgHTMLAttributes } from 'react';
import React, { useEffect, useState } from 'react';
import {
  getSourceUrlWithAssetToken,
  useAssetTokenContext,
} from '../AssetTokenContext';
import { useUIElementResize } from '../UIElementResizeContext';
import * as styles from './ImageNode.module.scss';
import type { CarouselItem } from './types';
import { isUploadedCarouselItem } from './types';
import Slot from '../../components/Slot';
import addTestId from '../../utils/addTestId';

function sanitize(url: string) {
  return sanitizeLink(url, ['https', 'data']) ? url : '//:0';
}

export const ALT = 'User-uploaded Image';
export const CAROUSEL_IMAGE_ATTRIBUTE = 'data-carousel-image';

function usePreloadedFullscreenImage(
  source: string | null,
  { disabled } = { disabled: false },
) {
  const [processedMap, setProcessedMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (disabled || !source) return;
    const fullscreenSrc = source.replace('/images/preload/', '/images/');
    if (fullscreenSrc !== source) {
      fetch(fullscreenSrc, { mode: 'no-cors' }).then(() =>
        setProcessedMap((existing) => ({
          ...existing,
          [source]: fullscreenSrc,
        })),
      );
    }
  }, [source, disabled]);

  return disabled || !source ? source : (processedMap[source] ?? source);
}

export const getImageSrc = (source: string, assetToken: string | null) =>
  getSourceUrlWithAssetToken(source, assetToken);

export const useImageSrc = (
  image: CarouselItem | null,
  { fullscreen }: { fullscreen: boolean },
) => {
  const { entityAssetToken } = useAssetTokenContext();

  const sanitizedSrc =
    image && isUploadedCarouselItem(image) ? sanitize(image.source) : null;

  const sourceWithAssetToken = sanitizedSrc
    ? getImageSrc(sanitizedSrc, entityAssetToken?.token ?? null)
    : null;

  const fullscreenSrc = usePreloadedFullscreenImage(sourceWithAssetToken, {
    disabled: !fullscreen,
  });

  return image && isUploadedCarouselItem(image)
    ? (fullscreenSrc ?? undefined)
    : image?.uploadPreviewSource;
};

interface ImageNodeProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'> {
  image: CarouselItem;
  fullscreen?: boolean;
}

const ImageNode = ({ image, fullscreen = false, ...props }: ImageNodeProps) => {
  const src = useImageSrc(image, { fullscreen });
  const onResize = useUIElementResize();

  return (
    <Slot
      onLoad={onResize}
      onError={onResize}
      className={styles.image}
      {...{ [CAROUSEL_IMAGE_ATTRIBUTE]: true }}
    >
      <img
        // Setting this allows us to select carousels in read mode
        tabIndex={-1}
        draggable={false}
        {...addTestId('imageCarouselImage')}
        {...props}
        src={src}
        alt={image.caption ?? ALT}
      />
    </Slot>
  );
};

export default ImageNode;
